import {
  pgTable,
  text,
  serial,
  integer,
  timestamp,
  jsonb,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Signature types
export const signatureTypes = ["open_enrollment", "qle", "waiver", "beneficiary_designation"] as const;
export type SignatureType = (typeof signatureTypes)[number];

// Signatures table - stores electronic signatures with S3 reference
export const signatures = pgTable("signatures", {
  id: serial("id").primaryKey(),

  // S3 storage
  s3Key: text("s3_key").notNull(), // Key to signature image in S3

  // Signer info
  signerName: text("signer_name").notNull(), // Typed full name
  signedAt: timestamp("signed_at").notNull(), // When they signed

  // Context
  employeeId: integer("employee_id").notNull(), // references employees.id
  clientId: integer("client_id").notNull(), // references clients.id

  // Type of signature event
  signatureType: text("signature_type").notNull(), // open_enrollment, qle, waiver, beneficiary_designation

  // What was signed (flexible JSON for audit trail)
  signedContent: jsonb("signed_content"), // { planTypes: [...], effectiveDate, waivers: [...], etc. }

  createdAt: timestamp("created_at").defaultNow(),
});

// Zod schema for validation
export const insertSignatureSchema = createInsertSchema(signatures, {
  s3Key: z.string().min(1, "S3 key is required"),
  signerName: z.string().min(1, "Signer name is required"),
  signedAt: z.date(),
  employeeId: z.number().positive("Employee ID is required"),
  clientId: z.number().positive("Client ID is required"),
  signatureType: z.enum(signatureTypes),
  signedContent: z.record(z.any()).optional().nullable(),
}).omit({
  id: true,
  createdAt: true,
});

// TypeScript types
export type Signature = typeof signatures.$inferSelect;
export type InsertSignature = z.infer<typeof insertSignatureSchema>;
